import React, { useState, useEffect } from "react";
import { ArrowLeft, Edit, Download, Share2, X, Eye, EyeOff, Truck } from "lucide-react";
import { toast } from "react-toastify";
import * as roldanApi from "../../../../../../ApiCalls/taktikApiCalls/roldanApi";
import { getWhatsappAccount } from "../../../../../../ApiCalls/mantisBusiness";
import { getColombiaDateString } from "../../../../../../utils/dateUtils";

const RemesaDetail = ({ mantisBusinessId, mantisBusinessUserId, token, remesaId, onBack, onEdit, onRefresh, onPreManifiestoSelect, user }) => {
  const [remesa, setRemesa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [creatingOrdenCarga, setCreatingOrdenCarga] = useState(false);
  const [creatingPreManifiesto, setCreatingPreManifiesto] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showWhatsappModal, setShowWhatsappModal] = useState(false);
  const [whatsappShareType, setWhatsappShareType] = useState("remesa"); // 'remesa' | 'ordenCarga'
  const [phoneNumber, setPhoneNumber] = useState("");
  const [sendingWhatsapp, setSendingWhatsapp] = useState(false);
  const [hideInfo, setHideInfo] = useState(false);

  useEffect(() => {
    loadRemesa();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remesaId]);

  const loadRemesa = async () => {
    setLoading(true);
    try {
      const res = await roldanApi.getRemesaById(mantisBusinessId, token, remesaId);
      if (res?.success) setRemesa(res.data);
    } catch (error) {
      console.error("Error loading remesa:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenWhatsappModal = (type = "remesa") => {
    setWhatsappShareType(type);
    const initialPhone = remesa?.celular || "";
    setPhoneNumber(initialPhone);
    setShowWhatsappModal(true);
  };

  const handleCloseWhatsappModal = () => {
    setShowWhatsappModal(false);
    setPhoneNumber("");
    setHideInfo(false);
    setWhatsappShareType("remesa");
  };

  const buildPreManifiestoDataFromRemesa = (r) => {
    const fechaCargue = r.fecha_cargue || r.fecha_creacion;
    const fechaViaje = fechaCargue
      ? new Date(fechaCargue).toISOString().split("T")[0]
      : getColombiaDateString();
    return {
      fecha_viaje: fechaViaje,
      origen: r.origen || "",
      origen_id: r.origen_id || "",
      destino: r.destino || "",
      destino_id: r.destino_id || "",
      estado: "Abierto",
    };
  };

  const handleCreatePreManifiestoFromRemesa = async () => {
    if (!remesa?.remesa_id || !onPreManifiestoSelect) return;
    setCreatingPreManifiesto(true);
    try {
      const preManifiestoData = buildPreManifiestoDataFromRemesa(remesa);
      const createRes = await roldanApi.createPreManifiesto(
        mantisBusinessId,
        mantisBusinessUserId,
        token,
        preManifiestoData
      );
      if (!createRes?.success || !createRes?.data?.pre_manifiesto_id) {
        toast.error(createRes?.message || "Error al crear el pre-manifiesto");
        return;
      }
      const preManifiestoId = createRes.data.pre_manifiesto_id;
      const assignRes = await roldanApi.assignRemesasToPreManifiesto(
        mantisBusinessId,
        token,
        preManifiestoId,
        [remesa.remesa_id]
      );
      if (!assignRes?.success) {
        toast.error(assignRes?.message || "Error al asignar la remesa al pre-manifiesto");
        return;
      }
      toast.success(`Pre-manifiesto creado y remesa ${remesa.numero_remesa} asignada`);
      if (onRefresh) onRefresh();
      loadRemesa();
      onPreManifiestoSelect(preManifiestoId);
    } catch (err) {
      console.error("Error creating pre-manifiesto from remesa:", err);
      toast.error("Error al crear el pre-manifiesto");
    } finally {
      setCreatingPreManifiesto(false);
    }
  };

  const handleCrearOrdenCarga = async () => {
    setCreatingOrdenCarga(true);
    try {
      const res = await roldanApi.createOrdenCarga(
        mantisBusinessId,
        token,
        remesaId,
        mantisBusinessUserId,
        user?.firstName + " " + user?.lastName
      );
      if (res?.success) {
        alert(res.warning || `Orden de carga creada para la remesa ${remesa.numero_remesa}`);
        loadRemesa();
      } else {
        alert(res?.message || res?.error || "Error al crear la orden de carga");
      }
    } catch (error) {
      console.error("Error creating orden de carga:", error);
      alert("Error al crear la orden de carga: " + (error.message || "Error desconocido"));
    } finally {
      setCreatingOrdenCarga(false);
    }
  };

  const handleShareWhatsApp = async () => {
    if (!phoneNumber || !phoneNumber.trim()) {
      alert("Por favor ingrese un número de teléfono");
      return;
    }

    setSendingWhatsapp(true);
    try {
      const whatsappAccountRes = await getWhatsappAccount(token, mantisBusinessId);

      if (!whatsappAccountRes.success || !whatsappAccountRes.account) {
        alert("No hay una cuenta de WhatsApp configurada. Por favor configure una cuenta en Configuración > Integraciones.");
        setSendingWhatsapp(false);
        return;
      }

      const whatsappAccount = whatsappAccountRes.account;

      if (whatsappShareType === "ordenCarga") {
        const res = await roldanApi.shareOrdenCargaWhatsApp(
          mantisBusinessId,
          token,
          remesaId,
          phoneNumber.trim(),
          whatsappAccount
        );
        if (res?.success) {
          alert(`Orden de carga ${remesa.orden_carga?.numero_orden || ""} compartida exitosamente a ${phoneNumber}`);
          handleCloseWhatsappModal();
        } else {
          alert(res?.error || res?.message || "Error al compartir la orden de carga");
        }
      } else {
        const res = await roldanApi.shareRemesaWhatsApp(
          mantisBusinessId,
          token,
          remesaId,
          phoneNumber.trim(),
          whatsappAccount,
          hideInfo
        );
        if (res?.success) {
          alert(`Remesa ${remesa.numero_remesa} compartida exitosamente a ${phoneNumber}`);
          handleCloseWhatsappModal();
        } else {
          alert(res?.error || "Error al compartir la remesa");
        }
      }
    } catch (error) {
      console.error("Error sharing:", error);
      alert("Error al compartir: " + (error.message || "Error desconocido"));
    } finally {
      setSendingWhatsapp(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Cargando...</div>;
  if (!remesa) return <div className="p-8 text-center text-red-600">Remesa no encontrada</div>;

  return (
    <div className="px-2">
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
        <div className="flex items-center justify-between gap-4 p-4 border-b border-gray-100">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900"
          >
            <ArrowLeft size={18} /> Volver
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => onEdit(remesaId)}
              className="inline-flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-gray-800 hover:bg-gray-50"
            >
              <Edit size={16} /> Editar
            </button>
            {!remesa.pre_manifiesto_id && onPreManifiestoSelect && (
              <button
                onClick={handleCreatePreManifiestoFromRemesa}
                disabled={creatingPreManifiesto}
                className="inline-flex items-center gap-2 px-3 py-2 border border-sky-200 rounded-lg bg-sky-50 text-sky-700 hover:bg-sky-100 disabled:opacity-50"
              >
                <Truck size={16} /> Crear pre-manifiesto desde esta remesa
              </button>
            )}
            <button
              onClick={() => handleOpenWhatsappModal("remesa")}
              className="inline-flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-gray-800 hover:bg-gray-50"
            >
              <Share2 size={16} /> Compartir Remesa por WhatsApp
            </button>
          </div>
        </div>

        <div className="p-4 grid gap-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-gray-100 p-4 bg-gray-50">
              <h2 className="text-sm font-bold text-gray-900 mb-2">
                Información General
              </h2>
              <div className="grid grid-cols-2 gap-2 text-sm text-gray-800">
                <div>
                  <span className="text-gray-500 font-semibold">
                    N° Remesa:
                  </span>{" "}
                  <span className="font-mono font-semibold text-gray-900">
                    {remesa.numero_remesa}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 font-semibold">Estado:</span>{" "}
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-semibold text-xs">
                    {remesa.estado}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 font-semibold">
                    Fecha Cargue:
                  </span>{" "}
                  {remesa.fecha_cargue
                    ? new Date(remesa.fecha_cargue).toLocaleDateString()
                    : "N/A"}
                </div>
                <div>
                  <span className="text-gray-500 font-semibold">
                    Hora Cargue:
                  </span>{" "}
                  {remesa.hora_cargue || "N/A"}
                </div>
                {remesa.origen && (
                  <div>
                    <span className="text-gray-500 font-semibold">Origen:</span>{" "}
                    {remesa.origen}
                  </div>
                )}
                {remesa.destino && (
                  <div>
                    <span className="text-gray-500 font-semibold">Destino:</span>{" "}
                    {remesa.destino}
                  </div>
                )}
              </div>
            </div>
            <div className="rounded-lg border border-gray-100 p-4 bg-gray-50">
              <h2 className="text-sm font-bold text-gray-900 mb-2">
                Cliente y Conductor
              </h2>
              <div className="grid grid-cols-2 gap-2 text-sm text-gray-800">
                <div>
                  <span className="text-gray-500 font-semibold">Cliente:</span>{" "}
                  {remesa.cliente || "N/A"}
                </div>
                {remesa.nit_cliente && (
                  <div>
                    <span className="text-gray-500 font-semibold">
                      NIT Cliente:
                    </span>{" "}
                    {remesa.nit_cliente}
                  </div>
                )}
                {remesa.direccion_cliente && (
                  <div className="col-span-2">
                    <span className="text-gray-500 font-semibold">
                      Dirección Cliente:
                    </span>{" "}
                    {remesa.direccion_cliente}
                  </div>
                )}
                {remesa.codigo_ciudad_cliente && (
                  <div>
                    <span className="text-gray-500 font-semibold">
                      Código Ciudad Cliente:
                    </span>{" "}
                    {remesa.codigo_ciudad_cliente}
                  </div>
                )}
                <div>
                  <span className="text-gray-500 font-semibold">
                    Conductor:
                  </span>{" "}
                  {remesa.nombre_conductor || "N/A"}
                </div>
                {remesa.cedula_conductor && (
                  <div>
                    <span className="text-gray-500 font-semibold">
                      CC Conductor:
                    </span>{" "}
                    {remesa.cedula_conductor}
                  </div>
                )}
                {remesa.placa_vehiculo && (
                  <div>
                    <span className="text-gray-500 font-semibold">Placa:</span>{" "}
                    {remesa.placa_vehiculo}
                  </div>
                )}
                {remesa.celular && (
                  <div className="col-span-2">
                    <span className="text-gray-500 font-semibold">
                      Celular:
                    </span>{" "}
                    {remesa.celular}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-100 p-4 bg-gray-50">
            <h2 className="text-sm font-bold text-gray-900 mb-2">
              Destinatario
            </h2>
            <div className="grid md:grid-cols-2 gap-2 text-sm text-gray-800">
              <div className="md:col-span-2">
                <span className="text-gray-500 font-semibold">Dirección:</span>{" "}
                {remesa.direccion_destinatario || "N/A"}
              </div>
              {remesa.fecha_descargue && (
                <div>
                  <span className="text-gray-500 font-semibold">
                    Fecha Descargue:
                  </span>{" "}
                  {new Date(remesa.fecha_descargue).toLocaleDateString()}
                </div>
              )}
              {remesa.hora_descargue && (
                <div>
                  <span className="text-gray-500 font-semibold">
                    Hora Descargue:
                  </span>{" "}
                  {remesa.hora_descargue}
                </div>
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-gray-100 p-4 bg-gray-50">
              <h2 className="text-sm font-bold text-gray-900 mb-2">Carga</h2>
              <div className="grid grid-cols-2 gap-2 text-sm text-gray-800">
                {remesa.peso_mercancia_kg && (
                  <div>
                    <span className="text-gray-500 font-semibold">Peso:</span>{" "}
                    {remesa.peso_mercancia_kg} kg
                  </div>
                )}
                {remesa.tipo_vehiculo && (
                  <div>
                    <span className="text-gray-500 font-semibold">
                      Tipo VH:
                    </span>{" "}
                    {remesa.tipo_vehiculo}
                  </div>
                )}
                {remesa.tipo_mercancia && (
                  <div className="col-span-2">
                    <span className="text-gray-500 font-semibold">
                      Tipo Mercancía:
                    </span>{" "}
                    {remesa.tipo_mercancia}
                  </div>
                )}
                {remesa.precinto && (
                  <div>
                    <span className="text-gray-500 font-semibold">
                      Precinto:
                    </span>{" "}
                    {remesa.precinto}
                  </div>
                )}
              </div>
            </div>
            <div className="rounded-lg border border-gray-100 p-4 bg-gray-50">
              <h2 className="text-sm font-bold text-gray-900 mb-2">
                Valores del Flete
              </h2>
              <div className="grid grid-cols-2 gap-2 text-sm text-gray-800">
                {remesa.flete && (
                  <div>
                    <span className="text-gray-500 font-semibold">Flete:</span>{" "}
                    ${parseFloat(remesa.flete || 0).toLocaleString()}
                  </div>
                )}
                {remesa.anticipo && (
                  <div>
                    <span className="text-gray-500 font-semibold">
                      Anticipo:
                    </span>{" "}
                    ${parseFloat(remesa.anticipo || 0).toLocaleString()}
                  </div>
                )}
                {remesa.porcentaje_anticipo && (
                  <div>
                    <span className="text-gray-500 font-semibold">
                      % Anticipo:
                    </span>{" "}
                    {parseFloat(remesa.porcentaje_anticipo || 0).toFixed(2)}%
                  </div>
                )}
              </div>
            </div>
          </div>

          {(remesa.satelital ||
            remesa.satelital_usuario ||
            remesa.observaciones ||
            remesa.creado_por_telefono) && (
            <div className="rounded-lg border border-gray-100 p-4 bg-gray-50">
              <h2 className="text-sm font-bold text-gray-900 mb-2">
                Información Adicional
              </h2>
              <div className="grid md:grid-cols-2 gap-2 text-sm text-gray-800">
                {remesa.satelital && (
                  <div>
                    <span className="text-gray-500 font-semibold">
                      Satélital:
                    </span>{" "}
                    {remesa.satelital}
                  </div>
                )}
                {remesa.satelital_usuario && (
                  <div>
                    <span className="text-gray-500 font-semibold">
                      Usuario Satélital:
                    </span>{" "}
                    {remesa.satelital_usuario}
                  </div>
                )}
                {remesa.satelital_contraseña && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 font-semibold">
                      Contraseña Satélital:
                    </span>{" "}
                    <span className="font-mono">
                      {showPassword ? remesa.satelital_contraseña : "••••••••"}
                    </span>
                    <button
                      onClick={() => setShowPassword(!showPassword)}
                      className="inline-flex items-center justify-center p-1 rounded hover:bg-gray-200 transition-colors"
                      type="button"
                      aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                    >
                      {showPassword ? (
                        <EyeOff size={16} className="text-gray-600" />
                      ) : (
                        <Eye size={16} className="text-gray-600" />
                      )}
                    </button>
                  </div>
                )}
                {remesa.creado_por_telefono && (
                  <div>
                    <span className="text-gray-500 font-semibold">
                      Teléfono Creador:
                    </span>{" "}
                    {remesa.creado_por_telefono}
                  </div>
                )}
                {remesa.observaciones && (
                  <div className="md:col-span-2">
                    <span className="text-gray-500 font-semibold">
                      Observaciones:
                    </span>{" "}
                    {remesa.observaciones}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Orden de Carga section - shows create button or orden info + actions */}
          <div className="rounded-lg border border-gray-100 p-4 bg-gray-50">
            <h2 className="text-sm font-bold text-gray-900 mb-2">Orden de Carga</h2>
            {!remesa.orden_carga ? (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={handleCrearOrdenCarga}
                  disabled={creatingOrdenCarga}
                  className="inline-flex items-center gap-2 px-3 py-2 border border-blue-200 bg-blue-50 text-blue-800 rounded-lg hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creatingOrdenCarga ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-800"></div>
                      Creando orden de carga...
                    </>
                  ) : (
                    <>Crear orden de carga</>
                  )}
                </button>
                <span className="text-sm text-gray-500">No hay orden de carga asociada a esta remesa.</span>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2 text-sm text-gray-800">
                  <div>
                    <span className="text-gray-500 font-semibold">Nº Orden:</span>{" "}
                    <span className="font-mono font-semibold text-gray-900">{remesa.orden_carga.numero_orden}</span>
                  </div>
                  {remesa.orden_carga.fecha_orden && (
                    <div>
                      <span className="text-gray-500 font-semibold">Fecha:</span>{" "}
                      {new Date(remesa.orden_carga.fecha_orden).toLocaleDateString()}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={async () => {
                      setDownloadingPDF(true);
                      try {
                        await roldanApi.downloadOrdenCargaPDF(mantisBusinessId, token, remesaId);
                      } catch (error) {
                        console.error("Error downloading PDF:", error);
                        alert(`Error al descargar el PDF: ${error.message || "Error desconocido"}`);
                      } finally {
                        setDownloadingPDF(false);
                      }
                    }}
                    disabled={downloadingPDF}
                    className="inline-flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {downloadingPDF ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                        Generando PDF...
                      </>
                    ) : (
                      <>
                        <Download size={16} /> Descargar Orden de Carga (PDF)
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => handleOpenWhatsappModal("ordenCarga")}
                    className="inline-flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-100"
                  >
                    <Share2 size={16} /> Compartir por WhatsApp
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="rounded-lg border border-gray-100 p-4 bg-gray-50">
            <h2 className="text-sm font-bold text-gray-900 mb-2">Documentos</h2>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={async () => {
                  setDownloadingPDF(true);
                  try {
                    await roldanApi.downloadRemesaPDF(mantisBusinessId, token, remesaId);
                  } catch (error) {
                    console.error("Error downloading PDF:", error);
                    alert(`Error al descargar el PDF: ${error.message || "Error desconocido"}`);
                  } finally {
                    setDownloadingPDF(false);
                  }
                }}
                disabled={downloadingPDF}
                className="inline-flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {downloadingPDF ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                    Generando PDF...
                  </>
                ) : (
                  <>
                    <Download size={16} /> Descargar Remesa (PDF)
                  </>
                )}
              </button>
              {remesa.estado === "Manifestada" && remesa.pre_manifiesto_id && (
                <button 
                  onClick={async () => {
                    try {
                      // Get manifiesto ID from remesa - need to query manifiesto_remesas table
                      // For now, show a message that this requires manifiesto ID
                      alert("Por favor, acceda al manifiesto desde la lista de manifiestos para descargar el PDF");
                    } catch (error) {
                      console.error("Error downloading PDF:", error);
                      alert("Error al descargar el PDF");
                    }
                  }}
                  className="inline-flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-100"
                >
                  <Download size={16} /> Descargar Manifiesto (PDF)
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* WhatsApp Share Modal */}
      {showWhatsappModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={handleCloseWhatsappModal}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {whatsappShareType === "ordenCarga"
                  ? "Compartir Orden de Carga por WhatsApp"
                  : "Compartir Remesa por WhatsApp"}
              </h3>
              <button
                onClick={handleCloseWhatsappModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>
            <div className="mb-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Número de teléfono (con código de país)
                </label>
                <input
                  type="text"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="Ej: +573001234567"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={sendingWhatsapp}
                />
              </div>
              {whatsappShareType === "remesa" && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="hideInfo"
                    checked={hideInfo}
                    onChange={(e) => setHideInfo(e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    disabled={sendingWhatsapp}
                  />
                  <label htmlFor="hideInfo" className="ml-2 text-sm text-gray-700">
                    Esconder info
                  </label>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={handleCloseWhatsappModal}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                disabled={sendingWhatsapp}
              >
                Cancelar
              </button>
              <button
                onClick={handleShareWhatsApp}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={sendingWhatsapp || !phoneNumber.trim()}
              >
                {sendingWhatsapp ? "Enviando..." : "Enviar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RemesaDetail;
